import { generateToast } from '@components/toasting'
import { BaseModule } from '@core/base/module/base-module'
import { AccountService } from '@core/services/api-gateway'

export interface ISettingsTeamState extends IRemoteData {
    data: Record<string, TTeamMember>
}

const state = {
    loading: false,
    error: false,
    lastFetched: '',
    data: {},
} satisfies ISettingsTeamState

export class Team extends BaseModule<ISettingsTeamState> {
    constructor(state: ISettingsTeamState) {
        super(state, 'team.module')
    }

    async handleEmailAlerts(
        sendAlerts: boolean,
        userId: TTeamMember['user_id']
    ) {
        const data = this.store.get().data

        const newData = {
            ...data,
            [userId]: {
                ...data[userId],
                send_alerts: sendAlerts,
            },
        }

        this.store.setKey('data', newData)

        const result = await AccountService.setEmailAlerts(userId, sendAlerts)

        if (!result) {
            this.store.setKey('data', {
                ...this.store.get().data,
                [userId]: data[userId],
            })

            generateToast({
                type: 'error',
                data: 'Email alerts could not be updated',
                testId: 'email-alerts-error-toast',
            })
        }
    }

    async handleUserDelete(id: TTeamMember['user_id']) {
        const store = this.store

        const result = await AccountService.deleteUser(id)

        if (!result) {
            return 'User could not be deleted'
        }

        const users = Object.assign({}, store.get().data)

        Reflect.deleteProperty(users, id)

        store.setKey('data', users)
    }

    async loadTeamMembers(forced = false) {
        const store = this.store

        if (forced || !store.get().lastFetched) {
            store.setKey('loading', true)

            const result = await AccountService.getTeam()

            if (!result) {
                store.setKey('loading', false)
                store.setKey('error', true)
                return false
            } else {
                store.setKey('data', result as Record<string, TTeamMember>)
                store.setKey('lastFetched', Date.now())
                store.setKey('loading', false)
                return true
            }
        }
        return true
    }

    async inviteUser(form: TInviteUserForm) {
        const payload = {
            access_level: 'admin',
            email: form.email,
            enable_email_notification: !!form.email_alerts,
            enable_mfa: true,
            first_name: form.first_name,
            last_name: form.last_name,
            job_title: form.job_title,
        }

        return await AccountService.inviteUser(payload)
    }
}

export const SettingsTeamModule = new Team(state)
